import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import { LoadingButton } from "@mui/lab";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Container,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { IAccountData } from "src/Types/account.types";
import type { IPayableItem } from "src/Types/module/paymentableItem/itemList";
import type {
	IPaymentItemConfig,
	IPaymentItemConfigPayload,
} from "src/Types/module/paymentableItem/paymentItemConfig.types";

const PaymentItemConfig: React.FC = () => {
	const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
	const [formError, setFormError] = useState<string | null>(null);

	// Form State
	const [stallItem, setStallItem] = useState<number | "">("");
	const [sellerSessionItem, setSellerSessionItem] = useState<number | "">("");
	const [buyerSessionItem, setBuyerSessionItem] = useState<number | "">("");
	const [toabBuyerSessionItem, setToabBuyerSessionItem] = useState<number | "">("");
	const [visitorTicketItem, setVisitorTicketItem] = useState<number | "">("");
	const [onlineAccountId, setOnlineAccountId] = useState<number | "">("");
	const [autoOnlineMr, setAutoOnlineMr] = useState<number>(0);

	// 1. Fetch Current Config
	const {
		data: configData,
		isLoading: isLoadingConfig,
	} = useApiQuery<IPaymentItemConfig>({
		endPoint: "/api/admin/payment-items/config",
		queryKeys: ["PaymentItemConfig"],
	});

	const config = configData || null;

	// 2. Fetch Payment Items for each specific type
	// Field 1: Stall Item -> exhibitor
	const {
		data: exhibitorItems = [],
		isLoading: isLoadingExhibitorItems,
	} = useApiQuery<IPayableItem[]>({
		endPoint: "/api/admin/payment-items?type=exhibitor",
		queryKeys: ["PayableItems", "exhibitor"],
	});
	const exhibitorItemsList = Array.isArray(exhibitorItems) ? exhibitorItems : [];

	// Field 2: B2B Items -> b2b
	const {
		data: b2bItems = [],
		isLoading: isLoadingB2bItems,
	} = useApiQuery<IPayableItem[]>({
		endPoint: "/api/admin/payment-items?type=b2b",
		queryKeys: ["PayableItems", "b2b"],
	});
	const b2bItemsList = Array.isArray(b2bItems) ? b2bItems : [];

	// Field 3: Visitor Ticket Item -> visitor
	const {
		data: visitorItems = [],
		isLoading: isLoadingVisitorItems,
	} = useApiQuery<IPayableItem[]>({
		endPoint: "/api/admin/payment-items?type=visitor",
		queryKeys: ["PayableItems", "visitor"],
	});
	const visitorItemsList = Array.isArray(visitorItems) ? visitorItems : [];

	// 3. Fetch Accounts list
	const {
		data: accountsData,
		isLoading: isLoadingAccounts,
	} = useGetQueryWithTotal<IAccountData[]>({
		endPoint: "/api/fair/admin/account",
		queryKeys: ["Accounts"],
		params: { limit: 100, skip: 0 },
	});

	const accountsList: IAccountData[] = Array.isArray(accountsData?.data)
		? accountsData.data
		: [];

	// Sync config to form state
	useEffect(() => {
		if (config) {
			setStallItem(config.stall_item ?? "");
			setSellerSessionItem(
				config.seller_session_item_id ?? (config as any).b2b_reg_item ?? ""
			);
			setBuyerSessionItem(
				config.buyer_session_item_id ?? (config as any).b2b_reg_item ?? ""
			);
			setToabBuyerSessionItem(
				config.toab_buyer_session_item_id ??
					config.buyer_session_item_id ??
					(config as any).b2b_reg_item ??
					""
			);
			setVisitorTicketItem(config.visitor_ticket_item ?? "");
			setOnlineAccountId(config.online_account_id ?? "");
			setAutoOnlineMr(Number(config.auto_online_mr) === 1 ? 1 : 0);
		}
	}, [config]);

	// 4. Update Mutation
	const { mutate: updateConfig, isPending } = useApiMutation<
		IPaymentItemConfigPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/admin/payment-items/config",
		invalidateQueries: ["PaymentItemConfig"],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		if (!stallItem) {
			setFormError("Please select a Stall Service Item.");
			return;
		}

		if (!sellerSessionItem) {
			setFormError("Please select a Seller Session Item.");
			return;
		}

		if (!buyerSessionItem) {
			setFormError("Please select a Buyer Session Item.");
			return;
		}

		if (!toabBuyerSessionItem) {
			setFormError("Please select a TOAB Buyer Session Item.");
			return;
		}

		if (!visitorTicketItem) {
			setFormError("Please select a Visitor Ticket Service Item.");
			return;
		}

		if (!onlineAccountId) {
			setFormError("Please select an Online Collection Account.");
			return;
		}

		const payload: IPaymentItemConfigPayload = {
			stall_item: Number(stallItem),
			seller_session_item_id: Number(sellerSessionItem),
			buyer_session_item_id: Number(buyerSessionItem),
			toab_buyer_session_item_id: Number(toabBuyerSessionItem),
			visitor_ticket_item: Number(visitorTicketItem),
			online_account_id: Number(onlineAccountId),
			auto_online_mr: Number(autoOnlineMr) === 1 ? 1 : 0,
		};

		updateConfig(payload, {
			onSuccess: () => {
				setSavedSuccess(true);
				setTimeout(() => setSavedSuccess(false), 4000);
			},
			onError: (err: any) => {
				console.error(err);
				setFormError(err?.message || "Failed to update configuration.");
			},
		});
	};

	const handleReset = () => {
		if (config) {
			setStallItem(config.stall_item ?? "");
			setSellerSessionItem(
				config.seller_session_item_id ?? (config as any).b2b_reg_item ?? ""
			);
			setBuyerSessionItem(
				config.buyer_session_item_id ?? (config as any).b2b_reg_item ?? ""
			);
			setToabBuyerSessionItem(
				config.toab_buyer_session_item_id ??
					config.buyer_session_item_id ??
					(config as any).b2b_reg_item ??
					""
			);
			setVisitorTicketItem(config.visitor_ticket_item ?? "");
			setOnlineAccountId(config.online_account_id ?? "");
			setAutoOnlineMr(Number(config.auto_online_mr) === 1 ? 1 : 0);
			setFormError(null);
		}
	};

	if (isLoadingConfig && !config) {
		return <GlobalLoader />;
	}

	return (
		<>
			<Helmet>
				<title>BTTF - Payment Item Configuration</title>
			</Helmet>

			<Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
				<Card elevation={2} sx={{ borderRadius: 2 }}>
					<CardHeader
						title={
							<Typography variant="h4" fontWeight={700}>
								Payment Item Configuration
							</Typography>
						}
						subheader={
							config?.updated_at
								? `Last updated: ${dayjs(config.updated_at).format(
									"DD MMM, YYYY hh:mm A"
								)}`
								: undefined
						}
						sx={{ p: 2.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
					/>

					<CardContent sx={{ p: 3 }}>
						{savedSuccess && (
							<Alert
								severity="success"
								sx={{ mb: 2.5 }}
								onClose={() => setSavedSuccess(false)}
							>
								Configuration updated successfully!
							</Alert>
						)}

						{formError && (
							<Alert
								severity="error"
								sx={{ mb: 2.5 }}
								onClose={() => setFormError(null)}
							>
								{formError}
							</Alert>
						)}

						<Box component="form" onSubmit={handleSubmit}>
							<Grid container spacing={2.5}>
								{/* 1. Stall Service Item */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="stall-item-label" shrink>
											Stall Service Item
										</InputLabel>
										<Select
											labelId="stall-item-label"
											value={stallItem}
											label="Stall Service Item"
											notched
											onChange={(e) => setStallItem(Number(e.target.value))}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingExhibitorItems
														? "Loading items..."
														: "Choose Stall Item"}
												</em>
											</MenuItem>
											{exhibitorItemsList.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name} &nbsp;&bull;&nbsp; ৳
													{Number(item.amount).toLocaleString()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 2. Seller Session Item */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="seller-session-item-label" shrink>
											Seller Session Item
										</InputLabel>
										<Select
											labelId="seller-session-item-label"
											value={sellerSessionItem}
											label="Seller Session Item"
											notched
											onChange={(e) =>
												setSellerSessionItem(Number(e.target.value))
											}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingB2bItems
														? "Loading items..."
														: "Choose Seller Session Item"}
												</em>
											</MenuItem>
											{b2bItemsList.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name} &nbsp;&bull;&nbsp; ৳
													{Number(item.amount).toLocaleString()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 3. Buyer Session Item */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="buyer-session-item-label" shrink>
											Buyer Session Item
										</InputLabel>
										<Select
											labelId="buyer-session-item-label"
											value={buyerSessionItem}
											label="Buyer Session Item"
											notched
											onChange={(e) =>
												setBuyerSessionItem(Number(e.target.value))
											}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingB2bItems
														? "Loading items..."
														: "Choose Buyer Session Item"}
												</em>
											</MenuItem>
											{b2bItemsList.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name} &nbsp;&bull;&nbsp; ৳
													{Number(item.amount).toLocaleString()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 4. TOAB Buyer Session Item */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="toab-buyer-session-item-label" shrink>
											TOAB Buyer Session Item
										</InputLabel>
										<Select
											labelId="toab-buyer-session-item-label"
											value={toabBuyerSessionItem}
											label="TOAB Buyer Session Item"
											notched
											onChange={(e) =>
												setToabBuyerSessionItem(Number(e.target.value))
											}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingB2bItems
														? "Loading items..."
														: "Choose TOAB Buyer Session Item"}
												</em>
											</MenuItem>
											{b2bItemsList.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name} &nbsp;&bull;&nbsp; ৳
													{Number(item.amount).toLocaleString()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 5. Visitor Ticket Item */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="visitor-ticket-item-label" shrink>
											Visitor Ticket Item
										</InputLabel>
										<Select
											labelId="visitor-ticket-item-label"
											value={visitorTicketItem}
											label="Visitor Ticket Item"
											notched
											onChange={(e) =>
												setVisitorTicketItem(Number(e.target.value))
											}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingVisitorItems
														? "Loading items..."
														: "Choose Ticket Item"}
												</em>
											</MenuItem>
											{visitorItemsList.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name} &nbsp;&bull;&nbsp; ৳
													{Number(item.amount).toLocaleString()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 6. Online Account ID */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" required>
										<InputLabel id="online-account-label" shrink>
											Online Collection Account
										</InputLabel>
										<Select
											labelId="online-account-label"
											value={onlineAccountId}
											label="Online Collection Account"
											notched
											onChange={(e) =>
												setOnlineAccountId(Number(e.target.value))
											}
										>
											<MenuItem value="" disabled>
												<em>
													{isLoadingAccounts
														? "Loading accounts..."
														: "Choose Account"}
												</em>
											</MenuItem>
											{accountsList.map((ac) => (
												<MenuItem key={ac.ac_id} value={ac.ac_id}>
													{ac.ac_name} {ac.ac_bank_name ? `(${ac.ac_bank_name})` : ""} &nbsp;&bull;&nbsp; {ac.ac_type}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{/* 7. Auto Online Money Receipt */}
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small">
										<InputLabel id="auto-online-mr-label" shrink>
											Auto Online Money Receipt (auto_online_mr)
										</InputLabel>
										<Select
											labelId="auto-online-mr-label"
											value={autoOnlineMr}
											label="Auto Online Money Receipt (auto_online_mr)"
											notched
											onChange={(e) =>
												setAutoOnlineMr(Number(e.target.value))
											}
										>
											<MenuItem value={1}>Enabled (Auto Create MR)</MenuItem>
											<MenuItem value={0}>Disabled (No Auto MR)</MenuItem>
										</Select>
										<FormHelperText>
											Auto generate Money Receipt when online payment succeeds
										</FormHelperText>
									</FormControl>
								</Grid>
							</Grid>


							{/* Actions */}
							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
								<Button
									variant="outlined"
									onClick={handleReset}
									disabled={isPending}
								>
									Reset
								</Button>
								<LoadingButton
									type="submit"
									variant="contained"
									loading={isPending}
									startIcon={<SaveTwoToneIcon fontSize="small" />}
								>
									<span>Save Configuration</span>
								</LoadingButton>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</Container>
		</>
	);
};

export default PaymentItemConfig;
